import { ImeModeKinds } from "../ImeModeKinds";
import { DataValidationKinds } from "../DataValidationKinds";
import { AutoNotePageKinds } from "../AutoNotePageKinds";
import { AbstractPlainTextItem } from "./AbstractPlainTextItem";
import { TypeKeys } from "./TypeKeys";
import { IImeControllable } from "./IImeControllable";
import { IEmptyValidatable } from "./IEmptyValidatable";
import { INameGroupEmptyValidatable } from "./INameGroupEmptyValidatable";
import { IPaintableOnEditing } from "./IPaintableOnEditing";
import { PaintStyle } from "../../drawing";
import { IFontSizeAutoAdjustable } from "./IFontSizeAutoAdjustable";
import { ITextValidatable } from "./ITextValidatable";
import { IExternalScriptAssociatable } from "./IExternalScriptAssociatable";
import { IRelatedExternalScriptExecutable } from "./IRelatedExternalScriptExecutable";
import { IPatientProfileInformationApplicable } from "./IPatientProfileInformationApplicable";
import { PatientProfileInformation } from "../../patient/PatientProfileInformation";
import { PatientProfileItem } from "../../patient/PatientProfileItem";
import { IDesignatedDoctorNumberApplicable } from "./IDesignatedDoctorNumberApplicable";
import { AsteriskKinds } from "../AsteriskKinds";
import { ITextLengthValidatable } from "./ITextLengthValidatable";
import { IPasteRequestable } from "./IPasteRequestable";
import { IPasteApplicable } from "./IPasteApplicable";
import { RelationalActionParameter } from "./RelationalActionParameter";
import { MovableTextBoxInputMethodKinds } from "../inputMethodKinds";
import { MovableTextBoxInputMethodOptionKinds } from "../inputMethodOptionKinds";
import { IMksTemplateExecutable } from "./IMksTemplateExecutable";
import { IDynamicTemplateExecutable } from "./IDynamicTemplateExecutable";
import { IFreeInputable } from "./IFreeInputable";
import { IJoinTextAssosiatable } from "./IJoinTextAssosiatable";
import { ITextCopyable } from "./ITextCopyable";
import { INaturalTextSpecifiable } from "./INaturalTextSpecifiable";

export class MovableTextBoxItem extends AbstractPlainTextItem implements
  IImeControllable,
  IEmptyValidatable,
  INameGroupEmptyValidatable,
  IPaintableOnEditing,
  IFontSizeAutoAdjustable,
  ITextValidatable,
  IExternalScriptAssociatable,
  IRelatedExternalScriptExecutable,
  IPatientProfileInformationApplicable,
  IDesignatedDoctorNumberApplicable,
  ITextLengthValidatable,
  IPasteRequestable,
  IPasteApplicable,
  IMksTemplateExecutable,
  IDynamicTemplateExecutable,
  IFreeInputable,
  IJoinTextAssosiatable,
  ITextCopyable,
  INaturalTextSpecifiable {
  constructor(value: any) {
    super(TypeKeys.MovableTextBoxItem, value);
  }

  /** 入力方法 */
  public inputMethodKind: MovableTextBoxInputMethodKinds = MovableTextBoxInputMethodKinds.FreeInput;
  /** 入力方法の選択肢の種別 */
  public inputMethodOptionKind: MovableTextBoxInputMethodOptionKinds = MovableTextBoxInputMethodOptionKinds.Normal;
  public paintStyleOnEditing: PaintStyle = new PaintStyle();
  public imeMode: ImeModeKinds = ImeModeKinds.AllOn;
  public enableSingleEmptyCheck: boolean = false;
  public enableEmptyCheckConfirmation: boolean = false;
  public alertGroupKey: string = null;
  public enabledAutoAdjustFontSize: boolean = false;
  public dataValidationKind: DataValidationKinds = null;
  public autoNotePageSize: number = null;
  public autoNotePageKind: AutoNotePageKinds = null;
  public isHisInfo = false;
  /** 指定医番号の種別(マスタとフォーマットに文字列で埋め込まれているので、enum対応はしない) */
  public designatedDoctorNumberType: string = null;
  /** 伏字の種類(PDFにどう表示するかどうか) */
  public asteriskKind: AsteriskKinds = null;
  public pasteDestinations: RelationalActionParameter[] = [];
  public parentAlertInformations: RelationalActionParameter[] = [];
  /** MKSテンプレート */
  public mksTemplateFormats: RelationalActionParameter[] = [];
  public mksTemplateRegisteredId: string = null;
  /** DYNAMICテンプレート */
  public dynamicTemplateFormats: RelationalActionParameter[] = [];
  public dynamicTemplateRegisteredId: string = null;
  public enableNameGroupEmptyCheck: boolean = false;
  public isNaturalViewEnable: boolean;
  public naturalText: string;
 
  protected createCloneInstance(): MovableTextBoxItem {
    return new MovableTextBoxItem(this.originalValue);
  }

  protected applyMembers(clone: MovableTextBoxItem): void {
    super.applyMembers(clone);
    clone.inputMethodKind = this.cloneValue(this.inputMethodKind);
    clone.inputMethodOptionKind = this.cloneValue(this.inputMethodOptionKind);
    clone.paintStyleOnEditing = this.cloneValue(this.paintStyleOnEditing);
    clone.imeMode = this.cloneValue(this.imeMode);
    clone.enableSingleEmptyCheck = this.cloneValue(this.enableSingleEmptyCheck);
    clone.enableEmptyCheckConfirmation = this.cloneValue(this.enableEmptyCheckConfirmation);
    clone.alertGroupKey = this.cloneValue(this.alertGroupKey);
    clone.enabledAutoAdjustFontSize = this.cloneValue(this.enabledAutoAdjustFontSize);
    clone.dataValidationKind = this.cloneValue(this.dataValidationKind);
    clone.autoNotePageSize = this.cloneValue(this.autoNotePageSize);
    clone.autoNotePageKind = this.cloneValue(this.autoNotePageKind);
    clone.isHisInfo = this.cloneValue(this.isHisInfo);
    clone.designatedDoctorNumberType = this.cloneValue(this.designatedDoctorNumberType);
    clone.asteriskKind = this.cloneValue(this.asteriskKind);
    clone.pasteDestinations = this.cloneValue(this.pasteDestinations);
    clone.parentAlertInformations = this.cloneValue(this.parentAlertInformations);
    clone.mksTemplateFormats = this.cloneValue(this.mksTemplateFormats);
    clone.mksTemplateRegisteredId = this.cloneValue(this.mksTemplateRegisteredId);
    clone.dynamicTemplateFormats = this.cloneValue(this.dynamicTemplateFormats);
    clone.dynamicTemplateRegisteredId = this.cloneValue(this.dynamicTemplateRegisteredId);
    clone.enableNameGroupEmptyCheck = this.cloneValue(this.enableNameGroupEmptyCheck);
    clone.isNaturalViewEnable = this.cloneValue(this.isNaturalViewEnable);
    clone.naturalText = this.cloneValue(this.naturalText);
  }

  get shouldInputFree(): boolean {
    return this.inputMethodKind === MovableTextBoxInputMethodKinds.FreeInput;
  }

  set shouldInputFree(value: boolean) {
    this.inputMethodKind = value
      ? MovableTextBoxInputMethodKinds.FreeInput
      : MovableTextBoxInputMethodOptionKinds.getDefualtInputMethodKind(this.inputMethodOptionKind);
  }

  get isEmpty(): boolean {
    return (this.value === undefined || this.value === null || this.value === "");
  }

  getGroupSelectKey(): string {
    return null;
  }

  public setText(text: string): void {
    this.value = text;
  }

  getValueForExternalScript(): string {
    return this.value;
  }

  setValueFromExternalScript(value: string): void {
    this.value = value;
  }

  setDesignatedDoctorNumber(value: string): void {
    this.value = value;
  }

  handlePatientProfileInformationApplied(info: PatientProfileInformation): boolean {
    if (!info || !info.items) { return false; }
    // TODO:V1の仕様の確認
    if (!this.name || !this.isEditable || this.isHisInfo) { return false; }
    const found = info.items.find(item => item.name === this.name);
    if (!found) { return false; }
    this.value = found.value;
    return true;
  }

  createPatientProfileItem(): PatientProfileItem {
    if (!this.name || this.isHisInfo) { return null; }
    const item = new PatientProfileItem();
    item.name = this.name;
    item.value = this.value;
    return item;
  }

  adjustFontSize(): void {
    if (!this.enabledAutoAdjustFontSize) { return; }
    const adjustedFontSize = this.getAdjustedFontSize(this.textStyle.isMultiLine);
    this.textStyle.adjustedFontSize = adjustedFontSize;
  }

  applyPastedValue(text: string, isChecked: boolean): void {
    this.value = text;
    this.adjustFontSize();
  }

  isPasteApplicable(isQuoting: boolean): boolean {
    return true;
  }
  
  pasteTo(item: IPasteApplicable): void {
    const text = this.valueToText(this.value);
    item.applyPastedValue(text, undefined);
  }

  isPasteKeyMatch(parameter: RelationalActionParameter): boolean {
    return (parameter.key === this.name && parameter.subKey === null);
  }

  get isSuppressionActive(): boolean {
    return this.isEmpty;
  }

  isValueUpdated(item: any): boolean {
    if (!item) { return true; }
    if (this.isEmpty && item.isEmpty) { return false; }
    return this.value !== item.value;
  }

  isPatientProfileKeyMatched(key: string): boolean {
    return (this.name === key) && !this.isHisInfo;
  }

  getDisplayName(): string {
    return this.isHisInfo ? undefined : this.name;
  }

  shouldValidateText(): boolean {
    return this.isFreeInputable && this.inputMethodKind === MovableTextBoxInputMethodKinds.FreeInput;
  }

  rollbackValue(): void {
    if (this.inputMethodKind === MovableTextBoxInputMethodKinds.None) {
      this.value = this.cloneValue(this.originalValue);
    }
  }

  get textToCopy(): string {
    return this.valueToText(this.value);
  }

  get currentNaturalText(): string {
    return this.isNaturalViewEnable && this.naturalText
      ? this.naturalText
      : this.text();
  }
}
